// Import Libraries
import React, { useContext } from 'react';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

// Import Components
import headerTheme from './headerTheme';
import ProjectTitle from './ProjectTitle';
import ScriptVersionSelect from '../Project/ScriptVersionSelect';
import CollaboratorAvatars from './CollaboratorAvatars';
import ViewToggle from './ViewToggle';
import ScenesToolbar from './ScenesToolbar';
import ShotsToolbar from './ShotsToolbar';
import ExportMenuButton from './ExportMenuButton';

// Context
import ScriptContext from '../../../context/Script';
import ProjectViewContext from '../../../context/ProjectView';

// Page gutters: 32px laptop, 24px iPad landscape, 16px iPad portrait.
const GUTTER = { xs: 2, md: 3, lg: 4 };
// Space between the two halves and the center line.
const HALF_INSET = { xs: 1, md: 2 };

// Construct Component
// Project header for tablet and desktop widths (phones keep ShotTableHeader).
// Row 1 holds project-level settings; row 2 holds the Scenes / Shots panel
// tools and is split exactly 50/50 to line up with the panels below.
const ProjectHeader = () => {
  const { script } = useContext(ScriptContext);
  const { view } = useContext(ProjectViewContext);

  const isProcessing = script && script.page_count !== script.last_processed_page;
  const processedPercent = isProcessing
    ? Math.round((script.last_processed_page / script.page_count) * 100)
    : 100;

  return (
    <ThemeProvider theme={headerTheme}>
      <Box component="header" sx={{ bgcolor: 'background.paper' }}>
        {/* Row 1: title, version, collaborators, view toggle */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          useFlexGap
          sx={{ px: GUTTER, py: 1, borderBottom: 1, borderColor: 'divider' }}
        >
          {/* Capped at the center line so a long title truncates instead of
              pushing the version select into the Shots half. */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            useFlexGap
            sx={{ minWidth: 0, maxWidth: 'calc(50% - 8px)', flex: '0 1 auto' }}
          >
            <ProjectTitle />
            <ScriptVersionSelect />
          </Stack>

          <Stack direction="row" justifyContent="center" sx={{ flex: '1 1 0', minWidth: 0 }}>
            <CollaboratorAvatars />
          </Stack>

          {isProcessing ? (
            <Stack spacing={0.5} sx={{ width: 160, flexShrink: 0 }}>
              <Typography variant="caption" color="text.secondary">
                Still processing · {processedPercent}%
              </Typography>
              <LinearProgress variant="determinate" value={processedPercent} />
            </Stack>
          ) : (
            <ViewToggle />
          )}
        </Stack>

        {/* Row 2: panel tools, split at exactly 50%. Export stays hidden
            until the script has finished processing. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: view.tableView ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))',
            alignItems: 'center',
            px: GUTTER,
            py: 1,
            bgcolor: '#FAFAFA',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          {view.tableView ? (
            // Table view has no Scenes / Shots panels, only the export.
            <Stack direction="row" justifyContent="flex-end">
              {!isProcessing && <ExportMenuButton />}
            </Stack>
          ) : (
            <>
              <Box sx={{ pr: HALF_INSET, minWidth: 0 }}>
                <ScenesToolbar />
              </Box>
              <Box sx={{ pl: HALF_INSET, minWidth: 0 }}>
                <ShotsToolbar showExport={!isProcessing} />
              </Box>
            </>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default ProjectHeader;
